import React, { useState, useEffect, useRef, useCallback } from 'react';
import { glassSurface } from './config/glassTheme';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import SideHelpPanel from './components/SideHelpPanel';
import ProductNav from './components/ProductNav';

// D&D / Fantasy category library \u2014 7 creation types
const DND_CATEGORIES = [
  { id: 'character-hero',       name: 'Character & Hero',        icon: '\u{1F9DD}', desc: 'Lineages, NPCs, adventurers, faction members, religious leaders, and named entities.' },
  { id: 'creature-monster',     name: 'Creature & Monster',       icon: '\u{1F409}', desc: 'Monsters, beasts, aberrations, dragons, undead, constructs, titans, and apex predators.' },
  { id: 'environment-location', name: 'Environment & Location',   icon: '\u{1F30D}', desc: 'Biomes, cities, planar regions, dungeons, temples, arcane sites, and cultural architecture.' },
  { id: 'magic-effects',        name: 'Magic & Effects',          icon: '\u2728', desc: 'Spells, rituals, wild magic, arcane phenomena, curses, and environmental corruption.' },
  { id: 'object-artifact',      name: 'Object & Artifact',        icon: '\u{1F48E}', desc: 'Weapons, magical items, relics, arcane devices, sigils, and spellbooks.' },
  { id: 'cinematic-narrative',  name: 'Cinematic & Narrative',    icon: '\u{1F3AC}', desc: 'Multi-character scenes \u2014 divine events, battles, rituals, cultural ceremonies, and mythic moments.' },
  { id: 'faction-civilization', name: 'Faction & Civilization',   icon: '\u269C\uFE0F', desc: 'Factions, orders, guilds, civilizations, political structures, and cultural organizations.' },
];

// Professional Generator category library \u2014 6 creation types
const PROFESSIONAL_CATEGORIES = [
  { id: 'hero',        name: 'Hero & Primary Visual',   icon: '\u{1F464}', desc: 'Portraits, professional talent, character heroes, campaign leads, and role-forward primary visuals.' },
  { id: 'product',     name: 'Product & Object',        icon: '\u{1F4E6}', desc: 'E-commerce renders, packaging, industrial objects, automotive, and product photography setups.' },
  { id: 'promotional', name: 'Promotional & Ad',        icon: '\u{1F4E3}', desc: 'Brand campaigns, marketing creatives, social ad formats, and commercial imagery systems.' },
  { id: 'editorial',   name: 'Editorial & Lifestyle',   icon: '\u{1F4F0}', desc: 'Magazine content, lifestyle photography, food, fashion, fitness, travel, and brand story.' },
  { id: 'interface',   name: 'Interface & Structured',  icon: '\u{1F5A5}\uFE0F', desc: 'UI/UX, infographics, data visualization, dashboards, and interface-ready visual assets.' },
  { id: 'thumbnail',   name: 'Thumbnail & Attention',   icon: '\u26A1', desc: 'YouTube covers, social thumbnails, course imagery, and high-attention scroll-stopping formats.' },
];

// Battlemaps category library \u2014 5 creation types
const BATTLEMAP_CATEGORIES = [
  { id: 'tactical-battlemaps', name: 'Tactical Battlemaps',  icon: '\u{1F5FA}\uFE0F', desc: 'Encounter-ready tactical maps for gameplay, combat, exploration, and VTT deployment.' },
  { id: 'cinematic-scenes',    name: 'Cinematic Scenes',      icon: '\u{1F3AC}', desc: 'Story-driven cinematic environments, reveals, rituals, siege moments, and campaign scenes.' },
  { id: 'structure-assets',    name: 'Structure Assets',      icon: '\u{1F3F0}', desc: 'Modular structures, fortifications, interiors, ruins, and architectural assets.' },
  { id: 'props-objects',       name: 'Props & Objects',       icon: '\u2697\uFE0F', desc: 'Encounter props, environmental storytelling objects, tactical assets, and world details.' },
  { id: 'terrain-tiles',       name: 'Terrain Tiles',         icon: '\u{1F33F}', desc: 'Reusable terrain systems, biome tiles, environmental segments, and map-building workflows.' },
];

// ---------------------------------------------------------------------------
// CATEGORY -> SECTION MAP  (explicit, static, human-maintainable)
//
// The backend taxonomy is domain -> section -> family -> generator. There is NO
// category tier in the data. The category tier exists only in the UI arrays
// above. This map assigns each REAL section slug (the deterministic output of
// the backend slugify(section_display_name)) to exactly one UI category.
//
// RULES:
//   - Keys are the category `id` values from the arrays above.
//   - Values are the REAL section slugs as stored in generator_search_index.
//   - Counts/filters below only ever use sections that ALSO appear in the live
//     taxonomy, so a slug listed here that has no indexed generators simply does
//     not show and does not inflate any count.
//   - This is a static assignment, NOT runtime word/substring/fuzzy matching.
//
// DND: all 48 known section slugs are assigned. A few are genuine judgement
// calls (deities / divine-hierarchy, transport, lairs-encounters-scenes,
// time-weather) \u2014 move a slug between arrays to re-file it; nothing else needs
// to change.
//
// PROFESSIONAL: complete. Each Professional engine declares its own section in
// its engine_identity.json; the generator stamps it onto every finished slug as
// meta.sectionSlug and the backend indexes it as section_slug. The map below is
// therefore one entry per ENGINE (six), not one per generator - publishing new
// slugs never requires editing it. It changes only if a Professional engine is
// added or removed.
// ---------------------------------------------------------------------------
const CATEGORY_SECTION_MAP = {
  dnd: {
    'character-hero': [
      'peoples-sentient-lineages',
      'classes-subclass-visual-archetypes',
      'npc-roles-professions-social-functions',
      'humanoids-ancestries',
      'identity-overlays',
      'role-profession-expansion-domains',
    ],
    'creature-monster': [
      'monsters-creatures-by-type',
      'creature-monster-overlay-domains',
      'creature-ecology-domains',
      'mounts-companions-summons',
    ],
    'environment-location': [
      'planar-cosmology-elemental-domains',
      'outer-planes-alignment-domains',
      'transitive-planes-border-domains',
      'environmental-state-domains',
      'settlement-structure-domains',
      'locations-environments-structures',
      'traps-hazards-dungeon-mechanics',
      'planes-cosmology-reality-layers',
      'time-weather-environmental-effects',
      'biome-micro-variant-domains',
    ],
    'magic-effects': [
      'magic-effects-templates',
      'chronology-temporal-domains',
      'arcane-theory-magical-domains',
      'planar-anomalies-phenomena',
      'magic-infusion-planar-expansion-domains',
      'spells-arcane-manifestations',
      'conditions-states-status-effects',
      'magical-system-domains',
    ],
    'object-artifact': [
      'armor-weapons-warfare',
      'items-gear-tools-equipment',
      'equipment-visual-archetype-domains',
      'artifact-relic-domains',
      'transport-travel-domains',
      'vehicles-transport-expanded',
      'artifacts-relics-legendary-objects',
    ],
    'cinematic-narrative': [
      'campaign-era-tone-domains',
      'lairs-encounters-scenes',
    ],
    'faction-civilization': [
      'divine-pantheons-theological-domains',
      'warfare-trade-infrastructure',
      'faction-organizational-domains',
      'social-structure-domains',
      'culture-civilization-domains',
      'factions-orders-organizations',
      'deities-pantheons-divine-aspects',
      'culture-society-daily-life',
      'economic-system-domains',
      'planar-politics-domains',
      'divine-hierarchy-domains',
    ],
  },
  professional: {
    // Derived from backend/data/professional_engines.json, not hand-maintained.
    //
    // Each Professional engine declares its own section in its engine_identity.json,
    // the generator stamps it onto every finished slug as meta.sectionSlug, and the
    // backend indexes it as section_slug. So this is a fixed 1:1 category <-> section
    // relationship: one entry per ENGINE, never one per generator. Producing new
    // slugs does NOT require editing this file - that is the whole point. It changes
    // only if a Professional engine is added or removed.
    'hero':        ['hero-primary-visual'],
    'product':     ['product-object'],
    'promotional': ['promotional-ad'],
    'editorial':   ['editorial-lifestyle'],
    'interface':   ['interface-structured'],
    'thumbnail':   ['thumbnail-attention'],
  },
};

// Intermediate (section / family) client-side grid page size.
const GRID_PAGE_SIZE = 24;
// Final generator server-side page size.
const GEN_PAGE_SIZE  = 48;

const SECTION_ICONS = {
  'PEOPLES & SENTIENT LINEAGES':               '\u{1F9DD}',
  'CLASSES & SUBCLASS VISUAL ARCHETYPES':      '\u2694\uFE0F',
  'NPC ROLES, PROFESSIONS & SOCIAL FUNCTIONS': '\u{1F9D1}\u200D\u{1F91D}\u200D\u{1F9D1}',
  'MONSTERS & CREATURES (BY TYPE)':            '\u{1F409}',
  'HUMANOIDS & ANCESTRIES':                    '\u{1F465}',
  'ARMOR, WEAPONS & WARFARE':                  '\u{1F6E1}\uFE0F',
  'ITEMS, GEAR, TOOLS & EQUIPMENT':            '\u{1F392}',
  'MAGIC, EFFECTS & TEMPLATES':                '\u2728',
  'PLANAR COSMOLOGY & ELEMENTAL DOMAINS':      '\u{1F30C}',
  'OUTER PLANES & ALIGNMENT DOMAINS':          '\u{1F300}',
  'TRANSITIVE PLANES & BORDER DOMAINS':        '\u{1F6AA}',
  'DIVINE PANTHEONS & THEOLOGICAL DOMAINS':    '\u26EA',
  'CHRONOLOGY & TEMPORAL DOMAINS':             '\u23F3',
  'ARCANE THEORY & MAGICAL DOMAINS':           '\u{1F52E}',
  'WARFARE, TRADE & INFRASTRUCTURE':           '\u2699\uFE0F',
  'PLANAR ANOMALIES & PHENOMENA':              '\u{1F4AB}',
  'IDENTITY OVERLAYS':                         '\u{1F3AD}',
  'EQUIPMENT & VISUAL ARCHETYPE DOMAINS':      '\u{1F527}',
  'MAGIC INFUSION & PLANAR EXPANSION DOMAINS': '\u{1F320}',
  'FACTION & ORGANIZATIONAL DOMAINS':          '\u269C\uFE0F',
  'ENVIRONMENTAL STATE DOMAINS':               '\u{1F33F}',
  'ROLE & PROFESSION EXPANSION DOMAINS':       '\u{1F4CB}',
  'SOCIAL STRUCTURE DOMAINS':                  '\u{1F3DB}\uFE0F',
  'CREATURE & MONSTER OVERLAY DOMAINS':        '\u{1F479}',
  'CAMPAIGN ERA & TONE DOMAINS':               '\u{1F4DC}',
  'SETTLEMENT & STRUCTURE DOMAINS':            '\u{1F3F0}',
  'ARTIFACT & RELIC DOMAINS':                  '\u{1F48E}',
  'TRANSPORT & TRAVEL DOMAINS':                '\u{1F5FA}\uFE0F',
  'CREATURE ECOLOGY DOMAINS':                  '\u{1F98E}',
  'CULTURE & CIVILIZATION DOMAINS':            '\u{1F3FA}',
  'LOCATIONS, ENVIRONMENTS & STRUCTURES':      '\u{1F5FB}',
  'SPELLS & ARCANE MANIFESTATIONS':            '\u{1F31F}',
  'CONDITIONS, STATES & STATUS EFFECTS':       '\u26A1',
  'FACTIONS, ORDERS & ORGANIZATIONS':          '\u{1F3F4}',
  'DEITIES, PANTHEONS & DIVINE ASPECTS':       '\u{1F320}',
  'MOUNTS, COMPANIONS & SUMMONS':              '\u{1F434}',
  'TRAPS, HAZARDS & DUNGEON MECHANICS':        '\u26A0\uFE0F',
  'VEHICLES & TRANSPORT (EXPANDED)':           '\u26F5',
  'LAIRS, ENCOUNTERS & SCENES':                '\u{1F5DD}\uFE0F',
  'CULTURE, SOCIETY & DAILY LIFE':             '\u{1F3D8}\uFE0F',
  'PLANES, COSMOLOGY & REALITY LAYERS':        '\u{1F52D}',
  'ARTIFACTS, RELICS & LEGENDARY OBJECTS':     '\u{1F3C6}',
  'TIME, WEATHER & ENVIRONMENTAL EFFECTS':     '\u{1F329}\uFE0F',
  'ECONOMIC SYSTEM DOMAINS':                   '\u{1F4B0}',
  'PLANAR POLITICS DOMAINS':                   '\u{1F4EF}',
  'DIVINE HIERARCHY DOMAINS':                  '\u{1F451}',
  'MAGICAL SYSTEM DOMAINS':                    '\u{1F4DA}',
  'BIOME MICRO-VARIANT DOMAINS':               '\u{1F332}',
};

function formatFamily(slug) {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const hpg = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
  tip:     { fontSize: 12, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', marginTop: 4 },
};

function getHelpContent(view) {
  const VIEW_HOME     = 'home';
  const VIEW_SECTIONS = 'sections';
  const VIEW_FAMILY   = 'family';
  const VIEW_RESULTS  = 'results'; // eslint-disable-line no-unused-vars

  const wrap = (content) => (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
      {content}
    </div>
  );

  if (view === VIEW_HOME) return {
    title: 'Generator Library',
    body: wrap(<>
      <div>
        <span style={hpg.heading}>What This Page Is</span>
        <p style={hpg.body}>Browse top-level generator categories.</p>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>How To Use</span>
        <ul style={hpg.list}>
          <li style={hpg.item}>Select a category card</li>
          <li style={hpg.item}>Use search if needed</li>
        </ul>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>Tip</span>
        <p style={hpg.tip}>Start broad, then narrow down.</p>
      </div>
    </>),
  };

  if (view === VIEW_SECTIONS) return {
    title: 'Category Overview',
    body: wrap(<>
      <div>
        <span style={hpg.heading}>What This Page Is</span>
        <p style={hpg.body}>This view shows sections within a selected category.</p>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>How To Use</span>
        <ul style={hpg.list}>
          <li style={hpg.item}>Select a section to continue</li>
        </ul>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>Tip</span>
        <p style={hpg.tip}>Categories organize large systems into usable groups.</p>
      </div>
    </>),
  };

  if (view === VIEW_FAMILY) return {
    title: 'Section View',
    body: wrap(<>
      <div>
        <span style={hpg.heading}>What This Page Is</span>
        <p style={hpg.body}>This page displays families within the selected section.</p>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>How To Use</span>
        <ul style={hpg.list}>
          <li style={hpg.item}>Choose a family</li>
          <li style={hpg.item}>Or use "Show All" to view all generators</li>
        </ul>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>Tip</span>
        <p style={hpg.tip}>Use "Show All" when you know what you're looking for.</p>
      </div>
    </>),
  };

  // VIEW_RESULTS (default)
  return {
    title: 'Generator Selection',
    body: wrap(<>
      <div>
        <span style={hpg.heading}>What This Page Is</span>
        <p style={hpg.body}>This page lists available generators within the selected family.</p>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>How To Use</span>
        <ul style={hpg.list}>
          <li style={hpg.item}>Select a generator to open it</li>
        </ul>
      </div>
      <div style={hpg.divider} />
      <div style={{ marginTop: 18 }}>
        <span style={hpg.heading}>Tip</span>
        <p style={hpg.tip}>Pick generators based on your desired output type.</p>
      </div>
    </>),
  };
}

export default function Generators() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useAuth();

  // Library switcher: 'dnd' | 'professional' | 'battlemaps'
  const initialLibrary = searchParams.get('library') === 'professional' ? 'professional' : searchParams.get('library') === 'battlemaps' ? 'battlemaps' : 'dnd';
  const [activeLibrary, setActiveLibrary] = useState(initialLibrary);

  // Pre-select category from URL param (e.g. ?library=battlemaps&category=tactical)
  const initialCategoryId = searchParams.get('category') || null;

  const VIEW_HOME     = 'home';
  const VIEW_SECTIONS = 'sections';
  const VIEW_FAMILY   = 'family';
  const VIEW_RESULTS  = 'results'; // eslint-disable-line no-unused-vars

  const getInitialCategory = (lib, catId) => {
    if (!catId) return null;
    if (lib === 'battlemaps')   return BATTLEMAP_CATEGORIES.find(c => c.id === catId) || null;
    if (lib === 'dnd')          return DND_CATEGORIES.find(c => c.id === catId) || null;
    if (lib === 'professional') return PROFESSIONAL_CATEGORIES.find(c => c.id === catId) || null;
    return null;
  };

  const hasStaticSections = (lib) => lib === 'battlemaps' || lib === 'dnd' || lib === 'professional';

  const [view,           setView]           = useState(hasStaticSections(initialLibrary) && initialCategoryId ? VIEW_SECTIONS : VIEW_HOME);
  const [activeCategory, setActiveCategory] = useState(getInitialCategory(initialLibrary, initialCategoryId));
  const [taxonomy,       setTaxonomy]       = useState([]);
  const [sectionCounts,  setSectionCounts]  = useState({});
  const [activeSection,  setActiveSection]  = useState('');
  const [activeFamily,   setActiveFamily]   = useState('');
  const [searchInput,    setSearchInput]    = useState('');
  const [autoResults,    setAutoResults]    = useState([]);
  const [showAuto,       setShowAuto]       = useState(false);
  const debouncedSearch = useDebounce(searchInput, 280);
  const searchRef = useRef(null);
  const [generators, setGenerators] = useState([]);
  const [grouped,    setGrouped]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Client-side pagination for the intermediate browse grids.
  const [sectionPage, setSectionPage] = useState(1); // section cards (category -> sections)
  const [familyPage,  setFamilyPage]  = useState(1); // family cards (section -> families)

  useEffect(() => {
    loadTaxonomy(initialLibrary);
    loadSectionCounts(initialLibrary);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Push a history entry so browser back works within the generator hierarchy
  function pushHistory(stateObj) {
    window.history.pushState(stateObj, '', window.location.pathname + window.location.search);
  }

  // Restore state from a browser back/forward pop. Intermediate grid pages are
  // reset to 1 for predictable behaviour rather than historical restoration.
  useEffect(() => {
    function onPop(e) {
      const st = e.state;
      if (!st || !st.__viGenerators) return;
      setView(st.view);
      setActiveLibrary(st.library);
      setActiveCategory(st.category || null);
      setActiveSection(st.section || '');
      setActiveFamily(st.family || '');
      setSearchInput(st.searchInput || '');
      setGenerators(st.generators || []);
      setGrouped(st.grouped || []);
      setTotal(st.total || 0);
      setPage(st.page || 1);
      setPages(st.pages || 1);
      setSectionPage(1);
      setFamilyPage(1);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When library switches, reload scoped taxonomy and section counts
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setTaxonomy([]);
    setSectionCounts({});
    loadTaxonomy(activeLibrary);
    loadSectionCounts(activeLibrary);
  }, [activeLibrary]); // eslint-disable-line react-hooks/exhaustive-deps

  // When library switches, return to home view
  function switchLibrary(lib) {
    pushHistory({ __viGenerators: true, view: VIEW_HOME, library: lib, category: null, section: '', family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setActiveLibrary(lib);
    setView(VIEW_HOME);
    setActiveCategory(null);
    setActiveSection('');
    setActiveFamily('');
    setSearchInput('');
    setGenerators([]);
    setGrouped([]);
    setTotal(0);
    setPage(1);
    setSectionPage(1);
    setFamilyPage(1);
  }

  async function loadTaxonomy(lib) {
    const domain = lib === 'professional' ? 'profession' : lib === 'battlemaps' ? 'battlemaps' : 'dnd';
    try {
      const res = await API.get('/v1/generators/taxonomy', { params: { domain } });
      setTaxonomy(res.data.taxonomy || []);
    } catch {}
  }

  async function loadSectionCounts(lib) {
    const domain = lib === 'professional' ? 'profession' : lib === 'battlemaps' ? 'battlemaps' : 'dnd';
    try {
      const res = await API.get('/v1/generators/section-counts', { params: { domain } });
      setSectionCounts(res.data.counts || {});
    } catch {}
  }

  useEffect(() => {
    if (debouncedSearch.length < 2) { setAutoResults([]); setShowAuto(false); return; }
    fetchAutocomplete(debouncedSearch);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAutocomplete(q) {
    try {
      const domain = activeLibrary === 'professional' ? 'profession' : activeLibrary === 'battlemaps' ? 'battlemaps' : 'dnd';
      const res = await API.get('/v1/search/autocomplete', { params: { q, domain } });
      const generators = res.data.generators || [];
      const families   = res.data.families   || [];
      const sections   = res.data.sections   || [];

      const results = [
        ...generators.map(g => ({
          name:    g.displayName,
          family:  g.familyDisplayName || '',
          section: g.sectionDisplayName || '',
          slug:    g.slug,
          type:    'generator',
        })),
        ...families.map(f => ({
          name:    f.familyDisplayName,
          family:  '',
          section: f.sectionDisplayName || '',
          slug:    null,
          type:    'family',
          family_slug: f.family,
        })),
        ...sections.map(sec => ({
          name:    sec.sectionDisplayName,
          family:  '',
          section: '',
          slug:    null,
          type:    'section',
          section_slug: sec.section,
        })),
      ];

      setAutoResults(results);
      setShowAuto(results.length > 0);
    } catch {
      setShowAuto(false);
    }
  }

  const loadGenerators = useCallback(async (opts = {}) => {
    const sec = opts.section !== undefined ? opts.section : activeSection;
    const fam = opts.family  !== undefined ? opts.family  : activeFamily;
    const q   = opts.q       !== undefined ? opts.q       : (debouncedSearch.length >= 2 ? debouncedSearch : '');
    const pg  = opts.page    !== undefined ? opts.page    : page;
    const lib = opts.library !== undefined ? opts.library : activeLibrary;

    setLoading(true);
    setError('');

    const params = {};
    if (sec) params.section  = sec;
    if (fam) params.family   = fam;
    if (q)   params.q        = q;
    params.page     = pg;
    params.per_page = GEN_PAGE_SIZE;
    if (lib === 'professional') params.domain = 'profession';
    if (lib === 'battlemaps')   params.domain = 'battlemaps';
    if (lib === 'dnd')          params.domain = 'dnd';          // B5

    try {
      const res = await API.get('/v1/generators', { params });
      const newGenerators = res.data.generators || [];
      const newGrouped    = res.data.grouped    || [];
      const newTotal      = res.data.total      || 0;
      const newPages      = res.data.pages      || 1;
      // Clamp: never leave the user on a page beyond the real last page.
      let   newPage       = res.data.page       || 1;
      if (newPage > newPages && newPages >= 1) {
        newPage = newPages;
        // Re-fetch the last valid page if the server returned an out-of-range page.
        if (pg !== newPages) {
          setLoading(false);
          return loadGenerators({ ...opts, page: newPages });
        }
      }
      setGenerators(newGenerators);
      setGrouped(newGrouped);
      setTotal(newTotal);
      setPage(newPage);
      setPages(newPages);
      setView(VIEW_RESULTS);
      pushHistory({ __viGenerators: true, view: VIEW_RESULTS, library: lib, category: activeCategory, section: sec, family: fam, searchInput: q, generators: newGenerators, grouped: newGrouped, total: newTotal, page: newPage, pages: newPages });
    } catch (err) {
      const status = err.response?.status;
      if (!status || (status !== 401 && !(status === 403 && err.response?.data?.error === 'account_restricted'))) {
        setError('Could not load generators. Please try again.');
      }
    }

    setLoading(false);
  }, [activeSection, activeFamily, debouncedSearch, page, activeLibrary]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCategoryClick(cat) {
    pushHistory({ __viGenerators: true, view: VIEW_SECTIONS, library: activeLibrary, category: cat, section: '', family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setActiveCategory(cat);
    setActiveSection('');
    setActiveFamily('');
    setSearchInput('');
    setView(VIEW_SECTIONS);
    setSectionPage(1);   // reset intermediate pagination for the new category
    setFamilyPage(1);
  }

  function handleSectionClick(sectionName) {
    pushHistory({ __viGenerators: true, view: VIEW_FAMILY, library: activeLibrary, category: activeCategory, section: sectionName, family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setActiveSection(sectionName);
    setActiveFamily('');
    setSearchInput('');
    setView(VIEW_FAMILY);
    setFamilyPage(1);    // reset family pagination for the new section
  }

  function handleFamilyClick(familySlug) {
    setActiveFamily(familySlug);
    setSearchInput('');
    setPage(1);
    loadGenerators({ section: activeSection, family: familySlug, q: '', page: 1 });
  }

  function handleSearchSubmit(e) {
    e && e.preventDefault();
    setShowAuto(false);
    if (!searchInput.trim()) return;
    setActiveSection('');
    setActiveFamily('');
    setPage(1);
    loadGenerators({ section: '', family: '', q: searchInput.trim(), page: 1 });
  }

  function handleAutoSelect(result) {
    setShowAuto(false);
    if (result.type === 'generator' && result.slug) {
      sessionStorage.setItem('active_slug', result.slug);
      navigate('/engine', { state: { slug: result.slug } });
      return;
    }
    const sec = result.section_slug || result.section || '';
    const fam = result.family_slug  || result.family  || '';
    setSearchInput(result.name);
    setActiveSection(sec);
    setActiveFamily(fam);
    setPage(1);
    loadGenerators({ section: sec, family: fam, q: result.name, page: 1 });
  }

  function goHome() {
    pushHistory({ __viGenerators: true, view: VIEW_HOME, library: activeLibrary, category: null, section: '', family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setView(VIEW_HOME);
    setActiveCategory(null);
    setActiveSection('');
    setActiveFamily('');
    setSearchInput('');
    setGenerators([]);
    setGrouped([]);
    setTotal(0);
    setSectionPage(1);
    setFamilyPage(1);
  }

  function goToSections() {
    pushHistory({ __viGenerators: true, view: VIEW_SECTIONS, library: activeLibrary, category: activeCategory, section: '', family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setView(VIEW_SECTIONS);
    setActiveSection('');
    setActiveFamily('');
    setGenerators([]);
    setGrouped([]);
    setSectionPage(1);   // land on a valid page for the section grid
    setFamilyPage(1);
  }

  function goToFamilies() {
    pushHistory({ __viGenerators: true, view: VIEW_FAMILY, library: activeLibrary, category: activeCategory, section: activeSection, family: '', searchInput: '', generators: [], grouped: [], total: 0, page: 1, pages: 1 });
    setActiveFamily('');
    setView(VIEW_FAMILY);
    setGenerators([]);
    setGrouped([]);
    setFamilyPage(1);    // land on a valid page for the family grid
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    loadGenerators({ page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openGenerator(gen) {
    sessionStorage.setItem('active_slug', gen.slug);
    sessionStorage.setItem('vi_navigating', '1');
    navigate('/engine', { state: { slug: gen.slug } });
  }

  useEffect(() => {
    function handler(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowAuto(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isProfessional = activeLibrary === 'professional';
  const isBattlemaps   = activeLibrary === 'battlemaps';
  const activeCategories = isBattlemaps ? BATTLEMAP_CATEGORIES : isProfessional ? PROFESSIONAL_CATEGORIES : DND_CATEGORIES;

  const activeTaxSection  = taxonomy.find(sec => sec.section === activeSection);
  const familiesInSection = activeTaxSection ? activeTaxSection.families : [];

  // The map for the active library ('battlemaps' has none \u2014 left untouched).
  const catMap = CATEGORY_SECTION_MAP[activeLibrary];

  // Real per-category stats from the explicit map, counting ONLY sections that
  // physically exist in the live taxonomy. A mapped section with no indexed
  // generators is excluded from both the section count and the generator total.
  const categoryStats = useCallback((cat) => {
    if (!catMap) return null;
    const slugs   = catMap[cat.id] || [];
    const present = slugs.filter(sl => taxonomy.some(sec => sec.section === sl));
    const generatorCount = present.reduce((sum, sl) => sum + (sectionCounts[sl] || 0), 0);
    return { sectionCount: present.length, generatorCount };
  }, [catMap, taxonomy, sectionCounts]);

  // Sections shown for the active category. dnd/professional use the explicit
  // map; Battlemaps uses its product-specific grouping and access flow.
  const activeSectionSlugs = (activeCategory && catMap) ? (catMap[activeCategory.id] || []) : null;
  const filteredTaxonomy = activeCategory
    ? (activeSectionSlugs
        ? taxonomy.filter(sec => activeSectionSlugs.includes(sec.section))
        : taxonomy.filter(sec => sec.section === activeCategory.id))
    : taxonomy;

  // Generator total for the active category header = sum of the real section
  // counts of exactly the sections shown.
  const activeCategoryGeneratorTotal = filteredTaxonomy.reduce(
    (sum, sec) => sum + (sectionCounts[sec.section] || 0), 0
  );

  // Dev aid: surface any live section not covered by the active map so unmapped
  // sections (especially Professional) are discoverable during validation.
  // This does NOT categorize anything at runtime; it only logs.
  useEffect(() => {
    if (!catMap || taxonomy.length === 0) return;
    const mapped = new Set(Object.values(catMap).flat());
    const orphans = taxonomy.map(s => s.section).filter(sl => !mapped.has(sl));
    if (orphans.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[Generators] Unmapped sections for library "' + activeLibrary + '":', orphans);
    }
  }, [catMap, taxonomy, activeLibrary]);

  // -------- Intermediate (client-side) pagination for section/family grids ----
  const sectionTotalPages = Math.max(1, Math.ceil(filteredTaxonomy.length / GRID_PAGE_SIZE));
  const sectionCurrent     = Math.min(sectionPage, sectionTotalPages);
  const sectionsPageItems  = filteredTaxonomy.slice(
    (sectionCurrent - 1) * GRID_PAGE_SIZE, sectionCurrent * GRID_PAGE_SIZE
  );

  const familyTotalPages = Math.max(1, Math.ceil(familiesInSection.length / GRID_PAGE_SIZE));
  const familyCurrent     = Math.min(familyPage, familyTotalPages);
  const familiesPageItems = familiesInSection.slice(
    (familyCurrent - 1) * GRID_PAGE_SIZE, familyCurrent * GRID_PAGE_SIZE
  );

  // Keep client page state clamped if the underlying list shrank.
  useEffect(() => {
    if (sectionPage > sectionTotalPages) setSectionPage(sectionTotalPages);
  }, [sectionTotalPages]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (familyPage > familyTotalPages) setFamilyPage(familyTotalPages);
  }, [familyTotalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  function pageTitle() {
    if (view === VIEW_RESULTS && searchInput && !activeSection) return `Search: "${searchInput}"`;
    if (view === VIEW_RESULTS && activeFamily)  return formatFamily(activeFamily);
    if (view === VIEW_RESULTS && activeSection) return activeSection;
    if (view === VIEW_RESULTS && activeCategory) return `${activeCategory.icon} ${activeCategory.name}`;
    return isBattlemaps ? 'Battlemaps Generator Library' : isProfessional ? 'Professional Generator Library' : 'D&D Generator Library';
  }

  // One consistent bounded pager for all deeper browse pages:
  //   Previous  1 ... window ... last  Next     Page X of Y
  function renderPager(current, totalPages, onChange) {
    if (totalPages <= 1) return null;
    const btns  = [];
    const start = Math.max(1, current - 2);
    const end   = Math.min(totalPages, current + 2);

    btns.push(
      <button key="prev"
        style={current <= 1 ? s.pageBtnDisabled : s.pageBtn}
        disabled={current <= 1}
        onClick={() => current > 1 && onChange(current - 1)}>prev</button>
    );
    if (start > 1) {
      btns.push(<button key={1} style={s.pageBtn} onClick={() => onChange(1)}>1</button>);
      if (start > 2) btns.push(<span key="dots1" style={{ color: '#64748b' }}>...</span>);
    }
    for (let i = start; i <= end; i++) {
      btns.push(<button key={i} style={i === current ? s.pageBtnActive : s.pageBtn} onClick={() => onChange(i)}>{i}</button>);
    }
    if (end < totalPages) {
      if (end < totalPages - 1) btns.push(<span key="dots2" style={{ color: '#64748b' }}>...</span>);
      btns.push(<button key={totalPages} style={s.pageBtn} onClick={() => onChange(totalPages)}>{totalPages}</button>);
    }
    btns.push(
      <button key="next"
        style={current >= totalPages ? s.pageBtnDisabled : s.pageBtn}
        disabled={current >= totalPages}
        onClick={() => current < totalPages && onChange(current + 1)}>next</button>
    );

    return (
      <div style={s.paginationWrap}>
        <div style={s.pagination}>{btns}</div>
        <div style={s.pageStatus}>Page {current} of {totalPages}</div>
      </div>
    );
  }

  function handleSectionPageChange(newPage) {
    setSectionPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function handleFamilyPageChange(newPage) {
    setFamilyPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div style={s.page}>

      <SideHelpPanel title={getHelpContent(view).title}>
        {getHelpContent(view).body}
      </SideHelpPanel>
      <ProductNav />

      <div className="vi-desktop-wide" style={s.container}>

        {/* Library Switcher */}
        <div style={s.libSwitcher}>
          <button
            style={!isProfessional && !isBattlemaps ? s.libBtnActive : s.libBtn}
            onClick={() => switchLibrary('dnd')}
          >
            {'\u{1F409}'} D&D Image Generators
          </button>
          <button
            style={isProfessional ? { ...s.libBtnActive, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)' } : s.libBtn}
            onClick={() => switchLibrary('professional')}
          >
            {'\u{1F4BC}'} Professional Generators
          </button>
          <button
            style={isBattlemaps ? { ...s.libBtnActive, background: 'linear-gradient(135deg, #D4AF37, #b8962e)' } : s.libBtn}
            onClick={() => switchLibrary('battlemaps')}
          >
            {'\u{1F5FA}\uFE0F'} Battlemaps
          </button>
        </div>


        <div style={s.searchWrap} ref={searchRef}>
          <form style={s.searchForm} onSubmit={handleSearchSubmit}>
            <input
              style={s.searchInput}
              placeholder={isBattlemaps ? 'Search battlemaps, families, or sections...' : isProfessional ? 'Search professional generators...' : 'Search D&D generators, families, or sections...'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => { if (autoResults.length > 0) setShowAuto(true); }}
              autoComplete="off"
            />
            <button type="submit" style={s.searchBtn}>Search</button>
          </form>
          {showAuto && (
            <div style={s.autoList}>
              {autoResults.map((r, i) => (
                <div key={i} style={s.autoItem}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                  onClick={() => handleAutoSelect(r)}>
                  <div>
                    <div style={s.autoName}>{r.name}</div>
                    <div style={s.autoFamily}>
                      {r.type === 'generator' && (r.family || r.section || '')}
                      {r.type === 'family'    && r.section}
                      {r.type === 'section'   && 'Section'}
                    </div>
                  </div>
                  {r.type !== 'generator' && (
                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 'auto', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {r.type}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {view !== VIEW_HOME && (
          <div style={s.breadcrumb}>
            <span style={s.crumb} onClick={goHome}>
              {isBattlemaps ? 'Battlemaps Library' : isProfessional ? 'Professional Library' : 'D&D Library'}
            </span>
            {activeCategory && (
              <>
                <span style={s.crumbSep}>{'>'}</span>
                {view === VIEW_SECTIONS
                  ? <span style={s.crumbActive}>{activeCategory.icon} {activeCategory.name}</span>
                  : <span style={s.crumb} onClick={goToSections}>{activeCategory.icon} {activeCategory.name}</span>
                }
              </>
            )}
            {activeSection && (
              <>
                <span style={s.crumbSep}>{'>'}</span>
                {view === VIEW_FAMILY
                  ? <span style={s.crumbActive}>{activeSection}</span>
                  : <span style={s.crumb} onClick={goToFamilies}>{activeSection}</span>
                }
              </>
            )}
            {activeFamily && (
              <>
                <span style={s.crumbSep}>{'>'}</span>
                <span style={s.crumbActive}>{formatFamily(activeFamily)}</span>
              </>
            )}
            {view === VIEW_RESULTS && !activeSection && !activeCategory && searchInput && (
              <>
                <span style={s.crumbSep}>{'>'}</span>
                <span style={s.crumbActive}>Search: "{searchInput}"</span>
              </>
            )}
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 8, padding: 16, color: '#fca5a5', marginBottom: 20 }}>
            {error}
          </div>
        )}

        {/* VIEW: HOME */}
        {view === VIEW_HOME && (
          <>
            <div style={{ marginBottom: 28 }}>
              {isBattlemaps ? (
                <>
                  <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>Battlemaps Generator Library</h1>
                  <p style={{ color: '#94a3b8', fontSize: 15, margin: 0 }}>Browse tactical maps, cinematic scenes, structures, props, and terrain across all available generators.</p>
                </>
              ) : isProfessional ? (
                <>
                  <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>Professional Generator Library</h1>
                  <p style={{ color: '#94a3b8', fontSize: 15, margin: 0 }}>Browse and access all available professional generators across six production creation types.</p>
                </>
              ) : (
                <>
                  <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>D&D Generator Library</h1>
                  <p style={{ color: '#94a3b8', fontSize: 15, margin: 0 }}>Browse characters, creatures, environments, battlemaps, magic, artifacts, and cinematic scenes. Select a category to browse, or search above.</p>
                </>
              )}
            </div>
            <div style={s.categoryGrid}>
              {activeCategories.map((cat, i) => {
                const stats = categoryStats(cat); // null for battlemaps (no map)
                return (
                <div key={i}
                  style={{ ...s.categoryTile }}
                  onClick={() => handleCategoryClick(cat)}
                  onMouseEnter={e => {
                    e.currentTarget.style.background  = isBattlemaps ? 'rgba(212,175,55,0.18)' : isProfessional ? 'rgba(14,165,233,0.18)' : 'rgba(124,58,237,0.18)';
                    e.currentTarget.style.borderColor = isBattlemaps ? 'rgba(212,175,55,0.55)' : isProfessional ? 'rgba(14,165,233,0.55)' : 'rgba(124,58,237,0.55)';
                    e.currentTarget.style.transform   = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background  = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.transform   = 'none';
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 30 }}>{cat.icon}</span>
                    <span style={s.liveBadge}>Live</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: '#fff' }}>{cat.name}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: '#94a3b8' }}>{cat.desc}</div>
                  {stats && (
                    <div style={{ marginTop: 10, color: isProfessional ? '#38bdf8' : '#a78bfa', fontSize: 12, fontWeight: 600 }}>
                      {stats.sectionCount} section{stats.sectionCount !== 1 ? 's' : ''} &bull; {stats.generatorCount.toLocaleString()} generators
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </>
        )}

        {/* VIEW: SECTIONS */}
        {view === VIEW_SECTIONS && activeCategory && (
          <>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>
                {activeCategory.icon} {activeCategory.name}
              </h1>
              <p style={{ color: '#94a3b8', fontSize: 15, margin: 0 }}>
                {filteredTaxonomy.length} section{filteredTaxonomy.length !== 1 ? 's' : ''} &bull; {activeCategoryGeneratorTotal.toLocaleString()} generators. Pick a section to browse, or search above.
              </p>
            </div>
            <div style={s.sectionGrid}>
              {filteredTaxonomy.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#64748b', padding: '60px 0', fontSize: 16 }}>
                  No generators currently indexed for this category.
                </div>
              ) : sectionsPageItems.map((sec, i) => {
                const count = sectionCounts[sec.section] || 0;
                return (
                  <div key={i} style={s.sectionTile}
                    onClick={() => handleSectionClick(sec.section)}
                    onMouseEnter={e => {
                      e.currentTarget.style.background  = isBattlemaps ? 'rgba(212,175,55,0.15)' : isProfessional ? 'rgba(14,165,233,0.15)' : 'rgba(124,58,237,0.15)';
                      e.currentTarget.style.borderColor = isBattlemaps ? 'rgba(212,175,55,0.5)'  : isProfessional ? 'rgba(14,165,233,0.5)' : 'rgba(124,58,237,0.5)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background  = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    }}>
                    <span style={s.sectionIcon}>{SECTION_ICONS[sec.section] || '\u{1F4C1}'}</span>
                    <div style={s.sectionName}>{sec.section}</div>
                    <div style={{ ...s.sectionCount, color: isBattlemaps ? '#D4AF37' : isProfessional ? '#38bdf8' : '#a78bfa' }}>
                      {sec.families.length} famil{sec.families.length !== 1 ? 'ies' : 'y'}
                      {count > 0 && ` - ${count.toLocaleString()} generators`}
                    </div>
                  </div>
                );
              })}
            </div>
            {renderPager(sectionCurrent, sectionTotalPages, handleSectionPageChange)}
          </>
        )}

        {/* VIEW: FAMILY */}
        {view === VIEW_FAMILY && (
          <>
            <div style={s.familyHeader}>
              <h1 style={{ ...s.familyTitle, marginBottom: 4 }}>{activeSection}</h1>
              <p style={s.familySubtitle}>
                {familiesInSection.length} famil{familiesInSection.length !== 1 ? 'ies' : 'y'}. Click a family to browse its generators, or search above.
              </p>
            </div>
            <button
              style={{ ...s.navBtn, marginBottom: 20, padding: '9px 18px', fontSize: 14, color: isBattlemaps ? '#D4AF37' : isProfessional ? '#38bdf8' : '#a78bfa', borderColor: isBattlemaps ? 'rgba(212,175,55,0.4)' : isProfessional ? 'rgba(14,165,233,0.4)' : 'rgba(124,58,237,0.4)' }}
              onClick={() => { setPage(1); loadGenerators({ section: activeSection, family: '', q: '', page: 1 }); }}>
              Show All in This Section ({(sectionCounts[activeSection] || 0).toLocaleString()} generators)
            </button>
            <div style={s.familyGrid}>
              {familiesPageItems.map((fam, i) => {
                const famSlug  = fam.family || fam;
                const famCount = (fam && typeof fam.count === 'number') ? fam.count : null;
                return (
                <div key={i} style={{ ...s.familyPill, ...(isBattlemaps ? { background: 'rgba(212,175,55,0.12)', borderColor: 'rgba(212,175,55,0.3)', color: '#fde68a' } : isProfessional ? { background: 'rgba(14,165,233,0.12)', borderColor: 'rgba(14,165,233,0.3)', color: '#bae6fd' } : {}) }}
                  onClick={() => handleFamilyClick(famSlug)}
                  onMouseEnter={e => {
                    e.currentTarget.style.background  = isBattlemaps ? 'rgba(212,175,55,0.22)' : isProfessional ? 'rgba(14,165,233,0.22)' : 'rgba(124,58,237,0.25)';
                    e.currentTarget.style.borderColor = isBattlemaps ? 'rgba(212,175,55,0.5)'  : isProfessional ? 'rgba(14,165,233,0.5)' : 'rgba(124,58,237,0.6)';
                    e.currentTarget.style.color       = isBattlemaps ? '#fef3c7' : isProfessional ? '#e0f2fe' : '#ede9fe';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background  = isBattlemaps ? 'rgba(212,175,55,0.12)' : isProfessional ? 'rgba(14,165,233,0.12)' : 'rgba(124,58,237,0.12)';
                    e.currentTarget.style.borderColor = isBattlemaps ? 'rgba(212,175,55,0.3)'  : isProfessional ? 'rgba(14,165,233,0.3)' : 'rgba(124,58,237,0.3)';
                    e.currentTarget.style.color       = isBattlemaps ? '#fde68a' : isProfessional ? '#bae6fd' : '#c4b5fd';
                  }}>
                  <span style={s.familyPillName}>{formatFamily(famSlug)}</span>
                  {famCount !== null && (
                    <span style={s.familyPillCount}>{famCount.toLocaleString()} generator{famCount !== 1 ? 's' : ''}</span>
                  )}
                </div>
                );
              })}
            </div>
            {renderPager(familyCurrent, familyTotalPages, handleFamilyPageChange)}
          </>
        )}

        {/* VIEW: RESULTS */}
        {view === VIEW_RESULTS && (
          <>
            {loading ? (
              <div style={s.spinner}>Loading generators...</div>
            ) : generators.length === 0 ? (
              <div style={s.empty}>No generators found. Try a different search or browse by section.</div>
            ) : (
              <>
                <div style={s.resultsHeader}>
                  <h2 style={s.resultsTitle}>{pageTitle()}</h2>
                  <span style={s.resultsCount}>
                    {total.toLocaleString()} generator{total !== 1 ? 's' : ''}
                    {pages > 1 && ` (page ${page} of ${pages})`}
                  </span>
                </div>
                {grouped.map((group, gi) => (
                  <div key={gi}>
                    {grouped.length > 1 && (
                      <div style={s.groupLabel}>{formatFamily(group.family)}</div>
                    )}
                    <div style={s.genGrid}>
                      {group.generators.map(gen => (
                        <div key={gen.id} style={s.genCard}
                          onClick={() => openGenerator(gen)}
                          onMouseEnter={e => {
                            e.currentTarget.style.borderColor = isBattlemaps ? 'rgba(212,175,55,0.5)' : isProfessional ? 'rgba(14,165,233,0.5)' : 'rgba(124,58,237,0.5)';
                            e.currentTarget.style.background  = isBattlemaps ? 'rgba(212,175,55,0.12)' : isProfessional ? 'rgba(14,165,233,0.12)' : 'rgba(124,58,237,0.12)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.background  = 'rgba(255,255,255,0.06)';
                          }}>
                          <p style={s.genName}>{gen.name}</p>
                          <div>
                            {gen.family && <span style={{ ...s.genFamily, ...(isBattlemaps ? { color: '#fbbf24', background: 'rgba(212,175,55,0.15)' } : isProfessional ? { color: '#38bdf8', background: 'rgba(14,165,233,0.15)' } : {}) }}>{formatFamily(gen.family)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {renderPager(page, pages, handlePageChange)}
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#fff' },
  nav: { background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 100 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#ccc', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  container: { maxWidth: 1200, margin: '0 auto', padding: '32px 40px' },

  // Library Switcher
  libSwitcher: { display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' },
  libBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  libBtnActive: { background: 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
  libComingSoonPill: { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.05em' },

  // Pro notice
  proNoticeBanner: { background: 'linear-gradient(90deg, rgba(14,165,233,0.15), rgba(99,102,241,0.15))', border: '1px solid rgba(14,165,233,0.3)', borderRadius: 10, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 14 },

  searchWrap: { position: 'relative', maxWidth: 680, margin: '0 auto 36px' },
  searchForm: { display: 'flex', gap: 8 },
  searchInput: { flex: 1, padding: '13px 18px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 16, outline: 'none' },
  searchBtn: { padding: '13px 22px', background: 'linear-gradient(135deg, #7c3aed, #db2777)', border: 'none', color: '#fff', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 },
  autoList: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e1b4b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, marginTop: 4, overflow: 'hidden', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' },
  autoItem: { padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 12 },
  autoName: { color: '#fff', fontWeight: 500, fontSize: 14 },
  autoFamily: { color: '#a78bfa', fontSize: 12, marginTop: 2 },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28, flexWrap: 'wrap' },
  crumb: { color: '#a78bfa', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' },
  crumbSep: { color: 'rgba(255,255,255,0.3)', fontSize: 14 },
  crumbActive: { color: '#fff', fontSize: 14, fontWeight: 600 },
  categoryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  categoryTile: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '20px 18px', cursor: 'pointer', transition: 'all 0.18s' },
  categoryTileDisabled: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', cursor: 'default' },
  liveBadge: { background: 'rgba(16,185,129,0.15)', color: '#10b981', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 },
  comingSoonBadge: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  sectionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  sectionTile: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '20px 18px', cursor: 'pointer', transition: 'all 0.18s' },
  sectionIcon: { fontSize: 28, marginBottom: 10, display: 'block' },
  sectionName: { fontWeight: 600, fontSize: 14, color: '#fff', lineHeight: 1.35, marginBottom: 6 },
  sectionCount: { color: '#a78bfa', fontSize: 12, fontWeight: 600 },
  familyHeader: { marginBottom: 20 },
  familyTitle: { fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  familySubtitle: { color: '#94a3b8', fontSize: 14 },
  familyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  familyPill: { background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', transition: 'all 0.15s', color: '#c4b5fd', fontSize: 13, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 4 },
  familyPillName: { fontSize: 13, fontWeight: 500 },
  familyPillCount: { fontSize: 11, fontWeight: 600, opacity: 0.85 },
  resultsHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 },
  resultsTitle: { fontSize: 20, fontWeight: 700, color: '#fff' },
  resultsCount: { color: '#94a3b8', fontSize: 14 },
  groupLabel: { fontSize: 13, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 0 8px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 10 },
  genGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 24 },
  genCard: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' },
  genName: { fontSize: 14, fontWeight: 600, color: '#fff', margin: '0 0 6px' },
  genFamily: { fontSize: 11, color: '#a78bfa', background: 'rgba(124,58,237,0.15)', display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontWeight: 600 },
  paginationWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 32 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
  pageStatus: { color: '#94a3b8', fontSize: 13, fontWeight: 600 },
  pageBtn: { padding: '8px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#ccc', cursor: 'pointer', fontSize: 14 },
  pageBtnActive: { padding: '8px 14px', borderRadius: 7, background: 'linear-gradient(135deg, #7c3aed, #db2777)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  pageBtnDisabled: { padding: '8px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.25)', cursor: 'default', fontSize: 14 },
  upgradeBtn: { ...glassSurface('#5B21B6', '#7c3aed', '#a855f7'), padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  empty: { textAlign: 'center', color: '#64748b', padding: '60px 0', fontSize: 16 },
  spinner: { textAlign: 'center', color: '#a78bfa', padding: '60px 0', fontSize: 16 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#fff', flexShrink: 0 },
};
